import { useCallback, useEffect, useRef, useState } from 'react';
import { dhlStatus, dhlLogin, dhlLog, dhlUebersicht, isDhlOn, setDhlOn, isDhlAnnahmeOn, setDhlAnnahmeOn, isDhlAusgabeOn, setDhlAusgabeOn, isDhlLabelMitNameOn, setDhlLabelMitNameOn, dhlNotiz, dt } from '../services/dhlBridge';
import {
  isWiesnAccount,
  isBevollmaechtigterEnabled,
  setBevollmaechtigterEnabled,
  isDhlStickEnabled,
  setDhlStickEnabled,
  getDhlStickIp,
  setDhlStickIp,
  getDhlStickRole,
  setDhlStickRole,
  getDhlStickStatus,
  saveDhlStickConfig,
  tapDhlStickCoord,
  wakeupDhlStick,
  clearDhlStickQueue,
} from '../services/dhlUsbStick';

const STICK_BUTTON_TARGETS = [
  { key: 'ausgabe', label: '📤 Ausgabe (Start)', xKey: 'ausgabeX', yKey: 'ausgabeY', defX: 33, defY: 38 },
  { key: 'schnell', label: '⚡ Schnelleinlagerung', xKey: 'schnellX', yKey: 'schnellY', defX: 33, defY: 56 },
  { key: 'empfaenger', label: '👤 Empfänger', xKey: 'empfaengerX', yKey: 'empfaengerY', defX: 33, defY: 20 },
  { key: 'bevollm', label: '👥 Bevollmächtigter', xKey: 'bevollmX', yKey: 'bevollmY', defX: 68, defY: 20 },
  { key: 'plus', label: '➕ Hinzufügen (+)', xKey: 'plusX', yKey: 'plusY', defX: 74, defY: 80 },
  { key: 'textfeld', label: '🔢 Sendungsnr.-Feld', xKey: 'textfeldX', yKey: 'textfeldY', defX: 35, defY: 18 },
  { key: 'weiter', label: '➡️ Weiter (unten rechts)', xKey: 'weiterX', yKey: 'weiterY', defX: 95, defY: 76 },
  { key: 'home', label: '🏠 Start (unten Mitte)', xKey: 'homeX', yKey: 'homeY', defX: 50, defY: 80 },
  { key: 'nameLast', label: '✍️ Nachname-Feld', xKey: 'nameLastX', yKey: 'nameLastY', defX: 35, defY: 18 },
  { key: 'nameFirst', label: '✍️ Vorname-Feld', xKey: 'nameFirstX', yKey: 'nameFirstY', defX: 35, defY: 29 },
];

/**
 * 🧪 Einstellungen → DHL-Anbindung (Experiment, nur freigeschaltete Konten). Zustand, Anmeldung im DHL-Portal
 * (wird nur durchgereicht), Bestandszahlen, Ein/Aus auf diesem Gerät, Live-Schalter mit Rückfrage, Protokoll.
 */
export default function DhlBridgePanel({ colors, language, onToggle, onClose }) {
  const T = dt(language);
  const dark = colors?.darkMode;
  const f = { text: dark ? '#fff' : '#23272f', sub: dark ? '#aaa' : '#666', rand: dark ? '#444' : '#ddd', feld: dark ? '#1b1f27' : '#fff', zeile: dark ? '#2d333b' : '#f5f6f8' };

  const [status, setStatus] = useState(null);
  const [lage, setLage] = useState(null);
  const [email, setEmail] = useState('');
  const [passwort, setPasswort] = useState('');
  const [meldetAn, setMeldetAn] = useState(false);
  const [loginMeldung, setLoginMeldung] = useState(null);
  const [an, setAn] = useState(() => isDhlOn());
  const [annahmeAn, setAnnahmeAnState] = useState(() => isDhlAnnahmeOn());
  const [ausgabeAn, setAusgabeAnState] = useState(() => isDhlAusgabeOn());
  const [liveFrage, setLiveFrage] = useState(null); // null | 'annahme' | 'ausgabe'
  const [labelMitName, setLabelMitNameState] = useState(() => isDhlLabelMitNameOn()); // Etikett erst drucken, wenn DHL den Namen zeigt
  const [bevollmAn, setBevollmAn] = useState(() => isBevollmaechtigterEnabled());
  const [stickOn, setStickOn] = useState(() => isDhlStickEnabled());
  const [stickIp, setStickIpState] = useState(() => getDhlStickIp());
  const [stickRole, setStickRoleState] = useState(() => getDhlStickRole());
  const [stickStatus, setStickStatus] = useState(null);
  const [stickCalOpen, setStickCalOpen] = useState(false);
  const [calMaxX, setCalMaxX] = useState(230);
  const [calMaxY, setCalMaxY] = useState(580);
  const [calCoords, setCalCoords] = useState({});
  const [selectedBtnKey, setSelectedBtnKey] = useState('weiter');
  const [stickMsg, setStickMsg] = useState('');
  const [log, setLog] = useState(null);
  const [offen, setOffen] = useState(null);
  const lebt = useRef(true);
  useEffect(() => () => { lebt.current = false; }, []);

  const ladeStickStatus = useCallback(async (ipOverride) => {
    if (!isWiesnAccount()) return;
    const st = await getDhlStickStatus(ipOverride || stickIp);
    if (!lebt.current) return;
    setStickStatus(st);
    if (st && st.ok) {
      if (st.maxX) setCalMaxX(st.maxX);
      if (st.maxY) setCalMaxY(st.maxY);
      if (st.coords) setCalCoords(st.coords);
    }
  }, [stickIp]);

  const ladeStatus = useCallback(async () => {
    setStatus(null);
    const s = await dhlStatus();
    if (!lebt.current) return;
    setStatus(s);
    if (s.browser === 'ok' && s.loggedIn) { const u = await dhlUebersicht(); if (lebt.current) setLage(u); }
  }, []);
  const ladeLog = useCallback(async () => { const r = await dhlLog(100); if (lebt.current) setLog(r.entries || []); }, []);
  useEffect(() => { ladeStatus(); ladeLog(); ladeStickStatus(); }, [ladeStatus, ladeLog, ladeStickStatus]);

  const activeTarget = STICK_BUTTON_TARGETS.find((t) => t.key === selectedBtnKey) || STICK_BUTTON_TARGETS[0];
  const curTargetX = calCoords[activeTarget.xKey] ?? activeTarget.defX;
  const curTargetY = calCoords[activeTarget.yKey] ?? activeTarget.defY;

  const handleFingerScreenTap = async (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const xPct = +Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100)).toFixed(1);
    const yPct = +Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100)).toFixed(1);
    const nextCoords = { ...calCoords, [activeTarget.xKey]: xPct, [activeTarget.yKey]: yPct };
    setCalCoords(nextCoords);
    setStickMsg(`🎯 ${activeTarget.label} → ${xPct}% / ${yPct}% gesetzt & geklickt...`);
    await saveDhlStickConfig({ [activeTarget.xKey]: xPct, [activeTarget.yKey]: yPct }, stickIp);
    await tapDhlStickCoord(xPct, yPct, stickIp);
    ladeStickStatus(stickIp);
  };

  const statusText = () => {
    if (!status) return T('laedt');
    if (status.reason === 'nicht_freigeschaltet') return '⛔ ' + T('stGesperrt');
    if (status.reason === 'offline') return '⚠️ ' + T('stOffline');
    if (status.browser !== 'ok') return '🔴 ' + T('stDown');
    if (status.loggedIn === false) return '🟡 ' + T('stLogin');
    if (status.busy) return '🟢 ' + T('stBusy');
    return '🟢 ' + T('stOk') + (status.shop ? ` · ${T('shop')}: ${status.shop}` : '');
  };
  const nichtAngemeldet = status && status.browser === 'ok' && status.loggedIn === false;

  const anmelden = async () => {
    setMeldetAn(true); setLoginMeldung(null);
    const r = await dhlLogin(email.trim(), passwort);
    if (!lebt.current) return;
    setMeldetAn(false); setPasswort('');
    setLoginMeldung(r.ok ? { ok: true, text: T('loginOk') } : { ok: false, text: `${T('loginFehler')}${r.message ? ': ' + r.message : ''}` });
    ladeStatus(); ladeLog();
  };
  const schalten = (on) => { setDhlOn(on); setAn(on); if (onToggle) onToggle(); };
  const liveSchalten = (welcher, on) => {
    if (welcher === 'annahme') { setDhlAnnahmeOn(on); setAnnahmeAnState(on); }
    else { setDhlAusgabeOn(on); setAusgabeAnState(on); }
    const label = welcher === 'annahme' ? 'Annahme' : 'Ausgabe';
    dhlNotiz('live_schalter', '', { ok: true, message: `${label} live buchen: ${on ? 'EIN (ausdrücklich bestätigt)' : 'AUS'}` }).then(ladeLog);
    if (onToggle) onToggle();
  };

  const feld = { width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${f.rand}`, fontSize: 15, background: f.feld, color: f.text, boxSizing: 'border-box', marginTop: 8 };
  const knopf = (primary) => ({ width: '100%', padding: '12px', borderRadius: 10, marginTop: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer', border: primary ? 'none' : `1px solid ${f.rand}`, background: primary ? '#d40511' : 'transparent', color: primary ? '#fff' : f.text });
  const klein = { padding: '6px 10px', borderRadius: 8, border: `1px solid ${f.rand}`, background: 'transparent', color: f.text, fontSize: 13, cursor: 'pointer' };
  const kopf = (txt, rechts) => (<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 18, marginBottom: 4 }}><b style={{ fontSize: 15 }}>{txt}</b>{rechts}</div>);
  const hinweis = (txt) => <div style={{ fontSize: 13, color: f.sub, marginTop: 6, lineHeight: 1.5 }}>{txt}</div>;
  const zeit = (iso) => { const d = new Date(iso); return `${d.toLocaleDateString(language, { day: '2-digit', month: '2-digit' })} ${d.toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`; };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 7600, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div data-testid="dhl-bridge-panel" style={{ background: dark ? '#23272f' : '#fff', color: f.text, borderRadius: 14, width: '100%', maxWidth: 560, maxHeight: '92vh', overflowY: 'auto', padding: 18, boxShadow: '0 10px 40px rgba(0,0,0,0.4)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <b style={{ fontSize: 18 }}>🧪 {T('titel')}</b>
          <button type="button" onClick={onClose} style={klein}>{T('schliessen')}</button>
        </div>
        {hinweis(T('experiment'))}

        {kopf(T('status'), <button type="button" data-testid="db-status-refresh" onClick={ladeStatus} style={klein}>{T('pruefen')}</button>)}
        <div data-testid="db-status" style={{ padding: '10px 12px', borderRadius: 8, background: f.zeile, fontSize: 14 }}>{statusText()}</div>

        {lage && lage.ok && (
          <div data-testid="db-heute">
            {kopf(T('heute'))}
            <div style={{ padding: '10px 12px', borderRadius: 8, background: f.zeile, fontSize: 14, lineHeight: 1.7 }}>
              <div>📦 {T('hKunden', { n: lage.kunden ?? '–' })} · 🚚 {T('hFahrer', { n: lage.fahrer ?? '–' })}{lage.vermist > 0 && <b style={{ color: '#c62828' }}> · {T('hVermist', { n: lage.vermist })}</b>}</div>
              {(lage.hinweise || []).map((h) => <div key={h} style={{ color: '#c62828' }}>⚠️ {h}</div>)}
            </div>
          </div>
        )}

        {nichtAngemeldet && (
          <div>
            {kopf(T('anmelden'))}
            <input data-testid="db-email" type="email" autoComplete="off" placeholder={T('email')} value={email} onChange={(e) => setEmail(e.target.value)} style={feld} />
            <input data-testid="db-passwort" type="password" autoComplete="off" placeholder={T('passwort')} value={passwort} onChange={(e) => setPasswort(e.target.value)} style={feld} />
            {hinweis(T('loginHinweis'))}
            <button type="button" data-testid="db-login" disabled={meldetAn || !email.trim() || !passwort} onClick={anmelden} style={{ ...knopf(true), opacity: meldetAn || !email.trim() || !passwort ? 0.6 : 1 }}>
              {meldetAn ? T('meldetAn') : T('uebertragen')}
            </button>
          </div>
        )}
        {loginMeldung && <div data-testid="db-login-meldung" style={{ marginTop: 8, fontSize: 14, color: loginMeldung.ok ? '#2e7d32' : '#c62828' }}>{loginMeldung.text}</div>}

        <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 18, fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
          <input data-testid="db-schalter" type="checkbox" checked={an} onChange={(e) => schalten(e.target.checked)} style={{ width: 20, height: 20 }} />
          {T('schalter')}
        </label>
        {hinweis(T('schalterHinweis'))}

        <div data-testid="db-live" style={{ marginTop: 16, padding: 12, borderRadius: 10, border: '2px solid #c62828' }}>
          <b style={{ fontSize: 15, color: '#c62828' }}>🔴 {T('liveTitel')}</b>
          <div style={{ fontSize: 13, color: f.text, marginTop: 4, lineHeight: 1.5 }}>{T('liveHinweis')}</div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, fontSize: 14, fontWeight: 600, cursor: an ? 'pointer' : 'default', opacity: an ? 1 : 0.5 }}>
            <input data-testid="db-schalter-annahme" type="checkbox" disabled={!an} checked={an && annahmeAn}
              onChange={(e) => (e.target.checked ? setLiveFrage('annahme') : liveSchalten('annahme', false))} style={{ width: 18, height: 18 }} />
            {T('schalterAnnahme')}{an && annahmeAn ? <span style={{ color: '#c62828' }}> · LIVE</span> : null}
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, fontSize: 14, fontWeight: 600, cursor: an ? 'pointer' : 'default', opacity: an ? 1 : 0.5 }}>
            <input data-testid="db-schalter-ausgabe" type="checkbox" disabled={!an} checked={an && ausgabeAn}
              onChange={(e) => (e.target.checked ? setLiveFrage('ausgabe') : liveSchalten('ausgabe', false))} style={{ width: 18, height: 18 }} />
            {T('schalterAusgabe')}{an && ausgabeAn ? <span style={{ color: '#c62828' }}> · LIVE</span> : null}
          </label>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 16, fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
          <input data-testid="db-schalter-label-name" type="checkbox" checked={labelMitName}
            onChange={(e) => { setDhlLabelMitNameOn(e.target.checked); setLabelMitNameState(e.target.checked); dhlNotiz('live_schalter', '', { ok: true, message: `Etikett erst mit Name: ${e.target.checked ? 'EIN' : 'AUS'}` }).then(ladeLog); }} style={{ width: 20, height: 20 }} />
          🖨️ {T('labelSchalter')}
        </label>
        {hinweis(T('labelSchalterHinweis'))}

        {isWiesnAccount() && (
          <>
            {/* 👥 Ausgabe an Bevollmächtigten (mit Name, Vorname & Vollmacht-Foto) */}
            <div style={{ marginTop: 16, padding: 12, borderRadius: 10, border: `1.5px solid ${bevollmAn ? '#7c3aed' : f.rand}`, background: f.zeile }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
            <input
              data-testid="db-schalter-bevollmaechtigter"
              type="checkbox"
              checked={bevollmAn}
              onChange={(e) => {
                setBevollmaechtigterEnabled(e.target.checked);
                setBevollmAn(e.target.checked);
                if (onToggle) onToggle();
              }}
              style={{ width: 20, height: 20 }}
            />
            👥 Ausgabe an Bevollmächtigten ermöglichen (mit Vollmacht-Foto)
          </label>
          <div style={{ fontSize: 13, color: f.sub, marginTop: 6, lineHeight: 1.5 }}>
            Standardmäßig aus. Wenn aktiv: Wisch nach rechts bleibt normale Ausgabe, zeigt aber 5 Sek. lang einen Button{' '}
            <b>„👥 An Bevollmächtigten"</b> (zusätzlich ein eigener <b>„Bevollm."</b>-Button am Paket). Fragt Vorname + Nachname des Bevollmächtigten, ein Foto der Vollmacht sowie 1 Unterschrift für alle ausgewählten Sendungen ab.
          </div>
        </div>

        {/* 🔌 Physischer DHL USB-Stick (ESP32-S3-GEEK: Maus+Tastatur + 2-Geräte-Queue + Finger-Kalibrierung) */}
        <div data-testid="db-usb-stick" style={{ marginTop: 16, padding: 14, borderRadius: 12, border: `2px solid ${stickStatus?.ok ? '#10b981' : '#f59e0b'}`, background: f.zeile }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            <b style={{ fontSize: 15 }}>🔌 DHL USB-Stick (SmartScanner-Steuerung & 2-Geräte-Queue)</b>
            <button type="button" onClick={() => ladeStickStatus(stickIp)} style={klein}>🔄 Stick prüfen</button>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={stickOn}
              onChange={(e) => {
                setDhlStickEnabled(e.target.checked);
                setStickOn(e.target.checked);
              }}
              style={{ width: 18, height: 18 }}
            />
            Sendungsnummern, Namen & Unterschrift automatisch an DHL USB-Stick senden
          </label>

          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ flex: '1 1 170px' }}>
              <div style={{ fontSize: 12, color: f.sub }}>WLAN-IP des DHL-Sticks:</div>
              <input
                type="text"
                value={stickIp}
                onChange={(e) => setStickIpState(e.target.value)}
                onBlur={() => { setDhlStickIp(stickIp); ladeStickStatus(stickIp); }}
                placeholder="192.168.137.51"
                style={{ ...feld, marginTop: 4, padding: '8px 10px', fontSize: 14 }}
              />
            </div>
            <div style={{ flex: '1 1 180px' }}>
              <div style={{ fontSize: 12, color: f.sub }}>Rolle dieses Scanners:</div>
              <select
                value={stickRole}
                onChange={async (e) => {
                  const r = Number(e.target.value) === 2 ? 2 : 1;
                  setStickRoleState(r);
                  setDhlStickRole(r);
                  await saveDhlStickConfig({ deviceRole: r }, stickIp);
                  ladeStickStatus(stickIp);
                }}
                style={{ ...feld, marginTop: 4, padding: '8px 10px', fontSize: 14 }}
              >
                <option value={1}>Scanner 1 (Schnelleinlagerung + Ausgabe)</option>
                <option value={2}>Scanner 2 (Ausgabe)</option>
              </select>
            </div>
          </div>

          {/* Live-Status des Sticks & der 2-Geräte-Warteschlange */}
          <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 8, background: f.feld, fontSize: 13, lineHeight: 1.55 }}>
            {!stickStatus ? (
              <span>⏳ Verbinde mit DHL-Stick ({stickIp})…</span>
            ) : stickStatus.ok ? (
              <>
                <div>
                  <b style={{ color: '#10b981' }}>🟢 Verbunden ({stickStatus.ip})</b> ·{' '}
                  {stickStatus.channelFree
                    ? <span style={{ color: '#10b981', fontWeight: 700 }}>Kanal frei</span>
                    : <span style={{ color: '#f59e0b', fontWeight: 700 }}>⏳ Belegt ({stickStatus.activeJobType || 'Aktiv'}, Warteschlange: {stickStatus.queueLength || 0})</span>}
                  {stickStatus.schnellFastPathReady ? ' · ⚡ Schnelleinl. bereit (Fast-Path)' : ''}
                </div>
                <div style={{ fontSize: 12, color: f.sub }}>
                  Auflösung: <b>{stickStatus.maxX}×{stickStatus.maxY} Steps</b> · Letzte Aktion: <code>{stickStatus.lastAction}</code> · Smart-Standby (90s + Auto-PIN 3537) aktiv
                </div>
              </>
            ) : (
              <span style={{ color: '#d97706' }}>⚠️ Stick unter {stickIp} momentan nicht erreichbar ({stickStatus.error || 'Offline'})</span>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
            <button
              type="button"
              onClick={async () => {
                setStickMsg('🔓 Wecke DHL-Scanner + gebe PIN 3537 ein…');
                await wakeupDhlStick(stickIp);
                setStickMsg('✅ Scanner aufgeweckt & entsperrt!');
                ladeStickStatus(stickIp);
              }}
              style={klein}
            >
              🔓 Scanner wecken + PIN
            </button>
            {stickStatus?.queueLength > 0 && (
              <button
                type="button"
                onClick={async () => {
                  await clearDhlStickQueue(stickIp);
                  ladeStickStatus(stickIp);
                }}
                style={klein}
              >
                🗑️ Warteschlange leeren ({stickStatus.queueLength})
              </button>
            )}
            <button
              type="button"
              onClick={() => setStickCalOpen((v) => !v)}
              style={{ ...klein, fontWeight: 700, borderColor: '#1976d2', color: '#1976d2' }}
            >
              🎯 {stickCalOpen ? 'Finger-Kalibrierung schließen' : 'Bildschirm & Knöpfe per Finger kalibrieren'}
            </button>
          </div>

          {stickMsg && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#10b981', fontWeight: 600 }}>{stickMsg}</div>
          )}

          {/* 🎯 Finger-Kalibrierung für andere Bildschirmauflösungen */}
          {stickCalOpen && (
            <div style={{ marginTop: 12, padding: 12, borderRadius: 10, border: `1px solid ${f.rand}`, background: f.feld }}>
              <b style={{ fontSize: 14, display: 'block', marginBottom: 6 }}>
                🎯 Bildschirm-Rand & Knopf-Positionen mit dem Finger anpassen
              </b>
              <div style={{ fontSize: 12, color: f.sub, lineHeight: 1.45, marginBottom: 10 }}>
                Hat ein anderer DHL-Scanner eine andere Auflösung? Stelle 1. den rechten/unteren Bildschirmrand (Max-X / Max-Y) ein und tippe 2. unten mit dem Finger dorthin, wo ein Knopf liegt:
              </div>

              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
                <label style={{ fontSize: 12 }}>
                  Rechter Rand (Max-X):{' '}
                  <input
                    type="number"
                    value={calMaxX}
                    onChange={(e) => setCalMaxX(Number(e.target.value) || 230)}
                    style={{ width: 70, padding: '5px 6px', borderRadius: 6, border: `1px solid ${f.rand}`, background: f.zeile, color: f.text }}
                  />
                </label>
                <label style={{ fontSize: 12 }}>
                  Unterer Rand (Max-Y):{' '}
                  <input
                    type="number"
                    value={calMaxY}
                    onChange={(e) => setCalMaxY(Number(e.target.value) || 580)}
                    style={{ width: 70, padding: '5px 6px', borderRadius: 6, border: `1px solid ${f.rand}`, background: f.zeile, color: f.text }}
                  />
                </label>
                <button
                  type="button"
                  onClick={async () => {
                    await saveDhlStickConfig({ maxX: calMaxX, maxY: calMaxY }, stickIp);
                    await tapDhlStickCoord(100, 100, stickIp);
                    setStickMsg(`✅ Auflösung ${calMaxX}×${calMaxY} gespeichert & Ecke unten-rechts (100/100) getestet!`);
                    ladeStickStatus(stickIp);
                  }}
                  style={klein}
                >
                  💾 Speichern & ↘️ Ecke testen
                </button>
              </div>

              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 12, color: f.sub, marginBottom: 4 }}>Welchen Knopf möchtest du per Finger-Tipp korrigieren?</div>
                <select
                  value={selectedBtnKey}
                  onChange={(e) => setSelectedBtnKey(e.target.value)}
                  style={{ ...feld, marginTop: 0, padding: '8px 10px', fontSize: 13 }}
                >
                  {STICK_BUTTON_TARGETS.map((b) => (
                    <option key={b.key} value={b.key}>
                      {b.label} (aktuell: {calCoords[b.xKey] ?? b.defX}% / {calCoords[b.yKey] ?? b.defY}%)
                    </option>
                  ))}
                </select>
              </div>

              {/* Interaktive Finger-Vorschau des DHL-Scanner-Displays */}
              <div
                onClick={handleFingerScreenTap}
                style={{
                  position: 'relative',
                  width: 220,
                  height: 390,
                  margin: '10px auto',
                  borderRadius: 14,
                  border: '3px solid #ffcc00',
                  background: 'linear-gradient(180deg, #e2e8f0 0%, #cbd5e1 100%)',
                  overflow: 'hidden',
                  cursor: 'crosshair',
                  touchAction: 'manipulation',
                  userSelect: 'none',
                }}
              >
                {/* Gelber DHL-Kopf */}
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '12%', background: '#ffcc00', color: '#d40511', fontWeight: 800, fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  DHL SmartScanner (Tippe für Position)
                </div>
                {/* Gelbe Nav-Leiste (Zurück / Start / Weiter bei Y=76%) */}
                <div style={{ position: 'absolute', top: '72%', left: 0, right: 0, height: '9%', background: '#ffcc00', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 8px', fontSize: 10, fontWeight: 700, color: '#000' }}>
                  <span>⬅️ 6/76</span>
                  <span>🏠 50/76</span>
                  <span>➡️ 95/76</span>
                </div>
                {/* Schwarze Android-Leiste unten */}
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '14%', background: '#0f172a', color: '#94a3b8', fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-around' }}>
                  <span>◀</span>
                  <span>●</span>
                  <span>■</span>
                  <span>⌨️</span>
                </div>
                {/* Alle gespeicherten Knopf-Punkte */}
                {STICK_BUTTON_TARGETS.map((b) => {
                  const bx = calCoords[b.xKey] ?? b.defX;
                  const by = calCoords[b.yKey] ?? b.defY;
                  const isSel = b.key === selectedBtnKey;
                  return (
                    <div
                      key={b.key}
                      style={{
                        position: 'absolute',
                        left: `${bx}%`,
                        top: `${by}%`,
                        transform: 'translate(-50%, -50%)',
                        padding: isSel ? '3px 6px' : '2px 4px',
                        borderRadius: 6,
                        background: isSel ? '#d40511' : 'rgba(15,23,42,0.72)',
                        color: '#fff',
                        fontSize: isSel ? 10 : 8,
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        border: isSel ? '2px solid #fff' : 'none',
                        zIndex: isSel ? 5 : 2,
                        pointerEvents: 'none',
                      }}
                    >
                      {b.label.split(' ')[0]} {bx}/{by}
                    </div>
                  );
                })}
              </div>
              <div style={{ fontSize: 11, color: f.sub, textAlign: 'center' }}>
                Ausgewählt: <b>{activeTarget.label}</b> bei <b>X={curTargetX}% / Y={curTargetY}%</b> — Tippe ins Bild, um den Punkt neu zu setzen & live am Scanner anzuklicken.
              </div>
            </div>
          )}
        </div>
          </>
        )}

        {liveFrage && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 7800, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div data-testid="db-live-frage" style={{ background: dark ? '#23272f' : '#fff', color: f.text, borderRadius: 14, width: '100%', maxWidth: 440, padding: 18, border: '2px solid #c62828', boxShadow: '0 10px 40px rgba(0,0,0,0.5)' }}>
              <b style={{ fontSize: 17, color: '#c62828' }}>🔴 {T('liveFrageTitel')}</b>
              <div style={{ fontSize: 14, marginTop: 10, lineHeight: 1.55 }}>{liveFrage === 'annahme' ? T('liveFrageAnnahme') : T('liveFrageAusgabe')}</div>
              <div style={{ fontSize: 13, color: f.sub, marginTop: 8, lineHeight: 1.5 }}>{T('liveFrageZusatz')}</div>
              <button type="button" data-testid="db-live-ja" onClick={() => { liveSchalten(liveFrage, true); setLiveFrage(null); }} style={{ ...knopf(true), background: '#c62828' }}>{T('liveJa')}</button>
              <button type="button" data-testid="db-live-nein" onClick={() => setLiveFrage(null)} style={knopf(false)}>{T('abbrechen')}</button>
            </div>
          </div>
        )}

        {kopf(T('protokoll'), <button type="button" data-testid="db-log-refresh" onClick={ladeLog} style={klein}>{T('neuLaden')}</button>)}
        <div data-testid="db-log">
          {log && log.length === 0 && hinweis(T('protokollLeer'))}
          {(log || []).map((e) => (
            <div key={e.id} onClick={() => setOffen(offen === e.id ? null : e.id)} style={{ padding: '8px 10px', borderRadius: 8, background: f.zeile, marginTop: 6, fontSize: 13, cursor: 'pointer' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <span><b style={{ color: e.ok ? '#2e7d32' : '#c62828' }}>{e.ok ? '✓' : '✗'}</b> {T(`op_${e.op}`)}{e.code ? ` · …${String(e.code).slice(-6)}` : ''}</span>
                <span style={{ color: f.sub, whiteSpace: 'nowrap' }}>{zeit(e.created_at)}{e.ms ? ` · ${(e.ms / 1000).toFixed(1)} s` : ''}</span>
              </div>
              {(e.reason || e.message) && <div style={{ color: e.ok ? f.sub : '#c62828', marginTop: 2 }}>{[e.reason, e.message].filter(Boolean).join(' — ')}</div>}
              {offen === e.id && e.detail && <pre style={{ margin: '6px 0 0', fontSize: 11, color: f.sub, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{JSON.stringify(e.detail, null, 1)}</pre>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
